import React, { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import EnrollmentService from "../../_services/EnrollmentService";
import { useEnrollmentSelection } from "./components/useEnrollmentSelection";

interface BulkTransferAlumniModalProps {
  show: boolean;
  onHide: () => void;
  selectedEnquiryIds: number[];
  enquiryEnrollmentsMap: Record<number, any[]>;
  studentList: any[];
  onSuccess: () => void;
}

interface Lookup {
  id: number;
  name: string;
}

const BulkTransferAlumniModal: React.FC<BulkTransferAlumniModalProps> = ({
  show,
  onHide,
  selectedEnquiryIds,
  enquiryEnrollmentsMap,
  studentList,
  onSuccess,
}) => {
  const { enrollments } = useEnrollmentSelection(
    selectedEnquiryIds,
    enquiryEnrollmentsMap,
    studentList,
    show,
  );

  const [years, setYears] = useState<Lookup[]>([]);
  const [loading, setLoading] = useState(false);
  const [passOutYearId, setPassOutYearId] = useState<number | "">("");
  const [remarks, setRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [summary, setSummary] = useState<any>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    setSummary(null);
    EnrollmentService.getPassoutYears()
      .then((resp) => {
        if (cancelled) return;
        setYears(resp.data?.data?.years || []);
      })
      .catch((err: any) => {
        if (!cancelled) setError(err?.response?.data?.message || "Failed to load pass out years");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [show]);

  const handleClose = () => {
    setPassOutYearId("");
    setRemarks("");
    setSummary(null);
    setError("");
    onHide();
  };

  const handleSubmit = async () => {
    setError("");
    if (!passOutYearId) {
      setError("Select a Pass Out Year.");
      return;
    }
    if (enrollments.length === 0) {
      setError("The selected students have no enrollments.");
      return;
    }
    setSubmitting(true);
    try {
      const resp = await EnrollmentService.bulkTransferToAlumni({
        enrollmentIds: enrollments.map((e) => e.enrollmentId),
        passOutYearId: Number(passOutYearId),
        remarks: remarks.trim() || null,
      });
      setSummary(resp.data?.data?.summary || null);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Failed to transfer to alumni");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDoneAfterResults = () => {
    handleClose();
    onSuccess();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>Transfer to Alumni</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <div
            style={{
              padding: "8px 12px",
              background: "var(--status-danger-bg)",
              color: "var(--status-danger)",
              borderRadius: 6,
              marginBottom: 12,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        {summary ? (
          <div style={{ display: "flex", gap: 12, fontSize: 13, color: "var(--text-strong)" }}>
            <span>
              <strong>{summary.total ?? 0}</strong> processed
            </span>
            <span style={{ color: "var(--status-success-text)" }}>
              <strong>{summary.created ?? 0}</strong> transferred
            </span>
            <span style={{ color: "var(--status-info-text)" }}>
              <strong>{summary.skipped ?? 0}</strong> skipped
            </span>
            <span style={{ color: "var(--status-danger)" }}>
              <strong>{summary.failed ?? 0}</strong> failed
            </span>
          </div>
        ) : loading ? (
          <div style={{ textAlign: "center", padding: 20, color: "var(--text-muted)" }}>
            <i className="fa fa-spinner fa-spin" /> Loading…
          </div>
        ) : (
          <>
            {/* Selected Students panel */}
            <div
              style={{
                background: "var(--surface-page)",
                border: "1px solid var(--border-default)",
                borderRadius: 8,
                padding: "12px 14px",
                marginBottom: 16,
                textAlign: "center",
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 8, color: "var(--text-strong)" }}>
                Selected Students
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "center" }}>
                {enrollments.map((e) => (
                  <span
                    key={e.enrollmentId}
                    style={{
                      display: "inline-block",
                      padding: "2px 10px",
                      borderRadius: 4,
                      background: "var(--status-danger)",
                      color: "#fff",
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    {e.studentCode || e.studentName}
                  </span>
                ))}
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>
                Pass Out Year <span style={{ color: "var(--status-danger)" }}>*</span>
              </Form.Label>
              <Form.Select
                size="sm"
                value={passOutYearId}
                onChange={(e) => setPassOutYearId(e.target.value ? Number(e.target.value) : "")}
              >
                <option value="">-- Select --</option>
                {years.map((y) => (
                  <option key={y.id} value={y.id}>
                    {y.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Remarks</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
              />
            </Form.Group>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        {summary ? (
          <Button variant="primary" onClick={handleDoneAfterResults}>
            Done
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={handleClose} disabled={submitting}>
              Close
            </Button>
            <Button
              variant="success"
              onClick={handleSubmit}
              disabled={submitting || loading || !passOutYearId || enrollments.length === 0}
            >
              {submitting ? (
                <>
                  <i className="fa fa-spinner fa-spin" /> Transferring…
                </>
              ) : (
                <>Transfer To Alumni</>
              )}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default BulkTransferAlumniModal;
